/**
 * Invitation code generator utility.
 * Generates short, human-friendly invitation codes for coupling partners.
 * Skips ambiguous characters (O, 0, I, 1) to reduce confusion.
 */
export class InvitationCode {
  /**
   * Available characters for invitation codes.
   * Excludes: O, 0, I, 1, D, Z, W, 2, 3, 8 (ambiguous characters)
   */
  private static readonly AVAILABLE_CHARACTERS = [
    "A",
    "B",
    "C",
    "E",
    "F",
    "G",
    "H",
    "J",
    "K",
    "L",
    "M",
    "N",
    "P",
    "Q",
    "R",
    "S",
    "T",
    "U",
    "V",
    "X",
    "Y",
    "4",
    "5",
    "6",
    "7",
    "9",
  ];

  /**
   * Generates a random invitation code with the specified number of characters.
   * @param numberOfCharacters The length of the code to generate
   * @returns A random uppercase invitation code
   */
  static generate(numberOfCharacters: number): string {
    let code = "";

    for (let i = 0; i < numberOfCharacters; i++) {
      const randomIndex = Math.floor(
        Math.random() * InvitationCode.AVAILABLE_CHARACTERS.length,
      );
      code += InvitationCode.AVAILABLE_CHARACTERS[randomIndex];
    }

    return code.toUpperCase();
  }

  /**
   * Generates a standard 6-character invitation code.
   */
  static generateStandard(): string {
    return InvitationCode.generate(6);
  }

  /**
   * Validates that a code contains only valid characters.
   * @param code The code to validate
   * @returns True if the code is valid
   */
  static isValid(code: string): boolean {
    if (!code || code.length === 0) {
      return false;
    }

    const upperCode = code.toUpperCase();
    for (let i = 0; i < upperCode.length; i++) {
      if (!InvitationCode.AVAILABLE_CHARACTERS.includes(upperCode.charAt(i))) {
        return false;
      }
    }

    return true;
  }

  /**
   * Normalizes an invitation code (uppercase + removes spaces/hyphens).
   * @param code The code to normalize
   * @returns The normalized code
   */
  static normalize(code: string): string {
    return code
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, "");
  }
}
